import authService from './authService';
import { Provider } from '../types/provider';
import { ApiResponse, PaginatedResponse } from '../types/common';
import {
  StockMovement,
  StockMovementSearchModel,
} from '../types/stockMovement';

// ─── Types ──────────────────────────────────────────────────────

export interface StockOverviewProduct {
  id: number;
  sku: string;
  description: string | null;
  stock_quantity: number | null;
  stock_quantity_available: number | null;
}

export interface ProviderWithProducts {
  provider: Provider;
  products: StockOverviewProduct[];
}

// ─── Service ────────────────────────────────────────────────────

class StockOverviewService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * Busca todos os fornecedores ativos que possuem produtos vinculados via ProviderProductPrice.
   * GET /api/reports/stock-overview/providers
   */
  async getProvidersWithProducts(): Promise<ProviderWithProducts[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/reports/stock-overview/providers');
    if (!response.ok) throw new Error('Erro ao carregar visão geral do estoque');
    const result: ApiResponse<ProviderWithProducts[]> = await response.json();
    return result.data ?? [];
  }

  /**
   * Busca movimentações de estoque de um produto específico (para exibição no expandir).
   * Reutiliza o endpoint existente de stock-movements.
   */
  async getMovementsByProduct(
    productId: number,
    searchModel: StockMovementSearchModel,
  ): Promise<PaginatedResponse<StockMovement>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
    });
    if (searchModel.originType !== undefined) params.append('origin_type', searchModel.originType.toString());
    if (searchModel.movementType !== undefined) params.append('movement_type', searchModel.movementType.toString());
    if (searchModel.startDate) params.append('start_date', searchModel.startDate);
    if (searchModel.endDate) params.append('end_date', searchModel.endDate);

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/stock-movements/product/${productId}?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar lançamentos de estoque do produto');

    // Backend returns camelCase, normalize to snake_case
    const result = await response.json();
    const rawData = result.data || [];
    const normalized = rawData.map((raw: any) => ({
      id: raw.stockMovementId,
      movement_date: raw.movementDate,
      origin_type: raw.orderId != null ? 1 : (raw.purchaseOrderId != null && raw.materialReceiptItemId == null ? 0 : raw.originType),
      origin_reference: raw.originReference,
      purchase_order_id: raw.purchaseOrderId,
      purchase_order_number: raw.purchaseOrderNumber,
      order_id: raw.orderId,
      order_number: raw.orderNumber,
      material_receipt_item_id: raw.materialReceiptItemId,
      material_receipt_id: raw.materialReceiptId,
      product_id: raw.productId,
      raw_material_id: raw.rawMaterialId,
      movement_type: raw.movementType,
      quantity: raw.quantity,
      previous_balance: raw.previousBalance,
      new_balance: raw.newBalance,
      purchase_price: raw.purchasePrice,
      cost_price: raw.costPrice,
      notes: raw.notes,
      created_at: raw.createdAt,
      updated_at: raw.updatedAt,
      created_by: raw.createdBy,
      last_updated_by: raw.lastUpdatedBy,
      product_description: raw.productDescription,
      product_sku: raw.productSku,
    }));

    return {
      ...result,
      data: normalized,
    };
  }
}

export const stockOverviewService = new StockOverviewService();
export default stockOverviewService;
